import { gqlMutation, withTooltip } from "@/common/lib";
import { deep_clone } from "@/common/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import {
  DeleteKeyListItemMutation,
  ResetSortKeyListItemMutation,
  SortKeyListItemMutation
} from "gql/mutations/key_list_item.graphql";
import React from "react";
import { firstBy } from "thenby";
import { GQLReport, GQLReportKeyListItem, GQLReportTrack } from "../../schemas/schema";
import BaseComponent from "../BaseComponent";
import { MarkdownWrapper, VersionTag } from "../common";
import { Comments, CommentsTag } from "../components";
import { ExistingKeyListItemModal } from "./ExistingKeyListItemModal";
import { KeyListEditModal } from "./KeyListEditModal";
import { ReportEditContext } from "../contexts/ReportEditContext";
import { NoPptPreview } from "@/admin/common/NoPptPreview";

class KeyListItemComments implements GQLReportKeyListItem {
  gid: string;
  id: string | undefined;
  report_key_list_id: string | undefined;
  description: string;
  impact: "Low" | "Medium" | "High";
  title: string;
  report_ref?: number;
  commentable_type: string;

  constructor(commentable_type: string, data: any) {
    this.commentable_type = commentable_type;
  }

  get commentable_id() {
    if (typeof this.report_key_list_id == "string") {
      return parseInt(this.report_key_list_id);
    }
    return this.report_key_list_id;
  }
}

export interface KeyListProps {
  className?: string
  report?: GQLReport
  report_track?: GQLReportTrack
  data: GQLObject[]
}

export class KeyList<T> extends BaseComponent<KeyListProps & T, any> {
  can_open_all_comments = false;
  object_type = "";
  object_type_verbose = "";
  object_type_verbose_plural = "";
  commentable_prefix = "";
  section_name = "";
  protected deleteConfirmingMessage = "This will permanently remove this item. Are you sure?";
  protected testid = "key-list-section";
  protected modalClass: any = KeyListEditModal;
  protected sortDirections: any[] = [1, -1];
  modalValidate: Function | null = null;
  deleteMutation = DeleteKeyListItemMutation;
  sortMutation = SortKeyListItemMutation;
  resetSortMutation = ResetSortKeyListItemMutation;

  static contextType = ReportEditContext;
  declare context: React.ContextType<typeof ReportEditContext>;

  protected get columns(): any[] {
    return [this.object_type_verbose, "Benefits", "Impact"];
  }

  protected get commentable_name() {
    return "";
  }

  protected get title() {
    return `Key ${this.object_type_verbose_plural} (${this.props.data?.length})`;
  }

  protected get report(): GQLReport | undefined {
    return undefined;
  }

  shouldComponentUpdate() {
    return true;
  }

  componentDidMount() {
    if (this.can_open_all_comments) {
      this.unsub_tokens.push(
        PubSub.subscribe("open.all.comments", () => {
          this.setState({
            showComments: this.props.data?.reduce((res, line) => ({ ...res, [`${line.id}`]: "open" }), {})
          });
          this.setState({ commentAutoFocus: false });
        })
      );
    }
  }

  componentWillUnmount() {
    this.unsub_tokens.map(PubSub.unsubscribe);
  }

  state = {
    editMode: true,
    editKeyListItem: undefined,
    existingModal: false,
    showComments: {},
    openComments: new Set<string>()
  } as any;

  editMode() {
    this.setState({ editMode: true });
  }

  reviewMode() {
    this.setState({ editMode: false });
  }

  switchMode() {
    this.setState({ editMode: !this.state.editMode });
  }

  wrapKeyListItem(line: GQLReportKeyListItem) {
    return new KeyListItemComments("ReportKeyList", line);
  }

  get descriptionFieldName() {
    return this.columns[1];
  }

  createKeyListItemFromModel(object) {
    const { cost, description, impact, priority, timeframe, title } = object;
    const editKeyListItem: GQLReportKeyListItem = {
      report_key_list_id: [this.props.report_track?.id, this.object_type].join("."),
      id: "0",
      gid: "0",
      title,
      priority,
      impact,
      description,
      cost,
      timeframe
    };
    this.setState({ editKeyListItem });
  }

  protected getData() {
    const data = deep_clone(this.props.data);
    return data.sort(firstBy((x: any) => x.sort_order));
  }

  protected renderHeaderAction() {
    return (
      <>
        <div className="create-link">
          <a onClick={() => this.existingModal()}>
            <FontAwesomeIcon icon={["fal", "plus-circle"]} />
            Add Existing
          </a>
        </div>
        <div className={"create-link"}>
          <a onClick={() => this.onCreate()} data-testid="add-new-button">
            <FontAwesomeIcon icon={["fal", "plus-circle"]} />
            Add New
          </a>
        </div>
        {gqlMutation(this.resetSortMutation, (mutateFunc) => (
          <div className="reset-sort">
            <a
              onClick={() =>
                mutateFunc({ variables: { gid: this.props.report_track?.gid, key_list_type: this.object_type } })
              }
            >
              <FontAwesomeIcon icon={["fal", "undo"]} />
              Reset Sort
            </a>
          </div>
        ))}
      </>
    );
  }

  protected renderHeader() {
    return (
      <div data-placement="left">
        <span className="report-indicator-title">
          {this.title}
        </span>
        <NoPptPreview section={this.section_name} />
        {this.renderHeaderAction()}
      </div>
    );
  }

  protected renderLineAction(line) {
    const data = this.getData();

    return (
      <div className="actions">
        {withTooltip(
          "Edit",
          <a onClick={() => this.openEdit(line)} data-testid="key-item-edit">
            <FontAwesomeIcon icon={["fal", "pencil"]} />
          </a>
        )}
        {withTooltip(
          "Comment",
          <a onClick={() => this.setComments(line, "all")} data-testid="key-item-comments">
            <FontAwesomeIcon icon={["fal", "comment"]} />
          </a>
        )}
        {gqlMutation(this.deleteMutation, (mutateFunc) =>
          withTooltip(
            "Delete",
            <a onClick={() => this.delete(line, mutateFunc)} data-testid="key-item-delete">
              <FontAwesomeIcon icon={["fal", "trash"]} />
            </a>
          )
        )}
        {gqlMutation(this.sortMutation, (mutateFunc) => (
          <div className="second-line">
            {data[0].id != line.id &&
              withTooltip(
                "Move up",
                <a data-testid="sortup" onClick={() => this.sort(line, mutateFunc, this.sortDirections[0])}>
                  <FontAwesomeIcon icon={["fal", "chevron-circle-up"]} />
                </a>
              )}
            {data[data.length - 1].id != line.id &&
              withTooltip(
                "Move down",
                <a data-testid="sortdown" onClick={() => this.sort(line, mutateFunc, this.sortDirections[1])}>
                  <FontAwesomeIcon icon={["fal", "chevron-circle-down"]} />
                </a>
              )}
          </div>
        ))}
      </div>
    );
  }

  protected renderLineContent(line) {
    return (
      <>
        <td>
          <p>{line.title}</p>
          <VersionTag className="ml-0" model={line} vKey="title" />
        </td>
        <td>
          <MarkdownWrapper>{line.description}</MarkdownWrapper>
          <VersionTag className="ml-0" model={line} vKey="description" />
        </td>
        {this.renderImpact(line)}
      </>
    );
  }

  protected renderLine(line) {
    return (
      <React.Fragment key={line.id}>
        <tr>
          {this.renderLineContent(line)}
          {this.state.editMode && <td>{this.renderLineAction(line)}</td>}
        </tr>
        <tr>
          <td colSpan={5}>
            <CommentsTag model={line} field={`${line.id}`} onOpen={(type) => this.setComments(line, type)} />
          </td>
        </tr>
        {this.state.showComments[line.id!] && (
          <tr>
            <td colSpan={5}>
              <Comments
                object={line}
                field={`${line.id}`}
                filterType={this.state.showComments[line.id!]}
                onChangeType={(type) => this.setComments(line, type)}
                autoFocus={this.state.commentAutoFocus}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  protected renderList() {
    const { editMode } = this.state;
    const data = this.getData();

    return (
      <div className={`key-list key-list-${this.object_type}`}>
        <table>
          <thead>
            <tr>
              {this.columns.map((column, i) => (
                <th key={i}>{column}</th>
              ))}
              {editMode && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>{data.map((line) => this.renderLine(line))}</tbody>
        </table>
      </div>
    );
  }

  protected getEditModalProps(): any {
    return {
      object_type_verbose: this.object_type_verbose,
      object_type_verbose_plural: this.object_type_verbose_plural,
      description_field_name: this.descriptionFieldName
    };
  }

  render() {
    const ModalClass = this.modalClass;

    return (
      <div className={cn("report-indicator-entry", this.props.className)} data-testid={this.testid}>
        <div className="report-indicator-info" id={this.commentable_prefix}>
          {this.state.editKeyListItem && (
            <ModalClass
              item={this.state.editKeyListItem}
              source={this.props.report_track}
              currency={this.props.report?.currency}
              onClose={() => this.setState({ editKeyListItem: undefined })}
              validate={this.modalValidate ? this.modalValidate : undefined}
              {...this.getEditModalProps()}
            />
          )}
          {this.state.existingModal && (
            <ExistingKeyListItemModal
              object_type={this.object_type}
              object_type_verbose={this.object_type}
              object_type_verbose_plural={this.object_type_verbose_plural}
              default_track={this.props.report_track?.track}
              onAdd={(model) => this.createKeyListItemFromModel(model)}
              onClose={() => this.setState({ existingModal: false })}
            />
          )}
          <div className="report-indicator-info-header">
            <div className="report-indicator-header">{this.renderHeader()}</div>
          </div>
          <div className="report-indicator-body">
            <div className={this.state.editMode ? "review-mode" : "edit-mode"}>{this.renderList()}</div>
          </div>
        </div>
      </div>
    );
  }

  focusComments(currentTarget: EventTarget & HTMLButtonElement): any {
    setTimeout(() => $(currentTarget).parents(".comment-line").find(".comment-input-box").trigger("focus"), 0);
  }

  toggleComments(line: GQLReportKeyListItem): void {
    const { openComments } = this.state;
    !openComments.has(line.id!) ? openComments.add(line.id!) : openComments.delete(line.id!);
    this.setState({ openComments });
  }

  delete<L extends GQLObject = GQLReportKeyListItem>(line: L, mutateFunc): void {
    if (!confirm(this.deleteConfirmingMessage)) {
      return;
    }
    mutateFunc({ variables: { gid: line.gid } });
  }

  protected sort<T>(line: any, mutateFunc: any, direction: T) {
    mutateFunc({ variables: { gid: line.gid, direction } });
  }

  openEdit(item: GQLReportKeyListItem): void {
    this.setState({ editKeyListItem: item });
  }

  protected onCreate(): void {
    const newKeyListItem: GQLReportKeyListItem = {
      id: "0",
      gid: "0",
      report_key_list_id: [this.props.report_track?.id, this.object_type].join("."),
      description: "",
      impact: "High",
      title: ""
    };
    this.setState({ editKeyListItem: newKeyListItem });
  }

  existingModal(): void {
    this.setState({ existingModal: true });
  }

  protected renderImpact(line: GQLReportKeyListItem) {
    return (
      <td className="impact">
        <p>{line.impact}</p>
        <VersionTag className="ml-0" model={line} vKey="impact" />
      </td>
    );
  }

  protected setComments(line: GQLReportKeyListItem, type: string, forced = false) {
    const { showComments } = this.state;

    if (forced) {
      this.setState({ showComments: { ...showComments, [line.id!]: type } });
    } else if (type === showComments[line.id!]) {
      this.setState({ showComments: { ...showComments, [line.id!]: undefined } });
    } else {
      this.setState({
        showComments: { ...showComments, [line.id!]: type }
      });
    }
  }
}
